import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import QRCode from "qrcode";
import { storeApi } from "./storeApi";
import { money, dateTime, timeAgo, countryName } from "./format";
import ShippingIcon, { deliveryInfo } from "./ShippingIcon";
import Icon from "./Icons";
import useSeo from "./useSeo";

// One page per order, reachable forever by its secret link: the thank-you
// right after checkout, payment instructions while unpaid, and live parcel
// tracking once it ships. /merch/track/<token> is the same page, tracking first.

const HEADLINE = {
  awaiting_payment: "Waiting for your payment",
  unfulfilled: "Paid. We're packing it.",
  fulfilled: "Packed and ready",
  shipped: "On its way",
  delivered: "Delivered",
  returned: "Returned",
  cancelled: "Cancelled",
};

// How often to look again, by state (only while the tab is visible).
const POLL = { awaiting_payment: 15000, unfulfilled: 120000, fulfilled: 120000, shipped: 60000 };

export default function OrderPage({ token, mode = "order" }) {
  const location = useLocation();
  const placed = new URLSearchParams(location.search).get("placed") === "1";
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [reload, setReload] = useState(0);
  useSeo({ title: order ? `Order #${order.number}` : "Your order", noindex: true });

  useEffect(() => {
    let timer;
    let live = true;
    const load = () =>
      storeApi
        .order(token)
        .then((o) => {
          if (!live) return;
          setOrder(o);
          setError(null);
          const wait = POLL[o.status];
          if (wait) timer = setTimeout(tick, wait);
        })
        .catch((e) => live && setError(e.message));
    const tick = () => (document.visibilityState === "hidden" ? (timer = setTimeout(tick, 5000)) : load());
    load();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [token, reload]);

  const act = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
      setReload((n) => n + 1);
    }
  };
  const payByCard = () =>
    act("card", async () => {
      const p = await storeApi.payOrder(token, "card");
      if (p.checkoutUrl) window.location.href = p.checkoutUrl;
    });
  const payByBank = () => act("bank", () => storeApi.payOrder(token, "bank"));
  const cancel = () => {
    if (window.confirm("Cancel this order? The items go back on sale.")) act("cancel", () => storeApi.cancelOrder(token));
  };

  if (error && !order) {
    return (
      <div className="sh-wrap sh-empty">
        <h1 className="sh-h1">We can't find that order</h1>
        <p>Check the link, or write to info@thecreaksofficial.com and we'll sort it out.</p>
        <Link className="sh-btn" to="/merch">
          Back to the store
        </Link>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="sh-wrap sh-empty">
        <span className="sh-spinner" /> <p>Loading your order…</p>
      </div>
    );
  }

  const thanks = placed && mode === "order" && order.status !== "cancelled";
  const track = <Tracking order={order} />;
  return (
    <div className="sh-wrap sh-order">
      <header className="sh-order-head">
        {thanks ? (
          <>
            <p className="sh-eyebrow">Order #{order.number}</p>
            <h1 className="sh-display sh-thanks">Thank you</h1>
            <p className="sh-order-lead">
              {order.status === "awaiting_payment"
                ? order.payment?.type === "card"
                  ? "Your card payment didn't go through yet. Your items are held for you below."
                  : "One last step: pay for your order below. Your items are held for you."
                : "Your order is confirmed. We'll pack it and send it your way."}
            </p>
          </>
        ) : (
          <>
            <p className="sh-eyebrow">
              Order #{order.number} · {dateTime(order.createdAt)}
            </p>
            <h1 className="sh-h1">{order.status === "shipped" && isReady(order) ? "Ready for pickup" : HEADLINE[order.status] || order.status}</h1>
          </>
        )}
      </header>

      <Progress order={order} />

      <div className="sh-order-grid">
        <div className="sh-order-main">
          {mode === "track" && track}
          {order.status === "awaiting_payment" && (
            <Payment order={order} busy={busy} onCard={payByCard} onBank={payByBank} />
          )}
          {error && (
            <div className="sh-alert" role="alert">
              <p>{error}</p>
            </div>
          )}
          {mode !== "track" && track}
          <SaveLink token={token} />
        </div>

        <aside className="sh-summary sh-order-summary">
          <h2 className="sh-h3">Your order</h2>
          <ul className="sh-mini">
            {order.items.map((i, n) => (
              <li key={n}>
                <span className="sh-mini-img">
                  {i.imageUrl && <img src={i.imageUrl} alt="" />}
                  <b>{i.qty}</b>
                </span>
                <span className="sh-mini-text">
                  {i.slug ? <Link to={`/merch/${i.slug}`}>{i.name}</Link> : <strong>{i.name}</strong>}
                  {i.refunded_qty > 0 && <span>{i.refunded_qty} refunded</span>}
                </span>
                <span>{money(i.price_minor * i.qty)}</span>
              </li>
            ))}
          </ul>
          <dl className="sh-totals">
            {order.discountMinor > 0 && (
              <div className="sh-total-discount">
                <dt>Discount</dt>
                <dd>−{money(order.discountMinor)}</dd>
              </div>
            )}
            <div>
              <dt>Delivery</dt>
              <dd>{order.shippingMinor ? money(order.shippingMinor) : "Free"}</dd>
            </div>
            <div className="sh-total">
              <dt>Total</dt>
              <dd>{money(order.totalMinor)}</dd>
            </div>
            {order.refundedMinor > 0 && (
              <div>
                <dt>Refunded</dt>
                <dd>−{money(order.refundedMinor)}</dd>
              </div>
            )}
          </dl>
          <Destination order={order} />
          <p className="sh-summary-note">
            Questions? <a href={`mailto:info@thecreaksofficial.com?subject=Order%20%23${order.number}`}>info@thecreaksofficial.com</a>, quote #{order.number}.
          </p>
          <div className="sh-order-links">
            <Link className="sh-textlink" to="/merch">
              Continue shopping
            </Link>
            {order.canCancel && (
              <button className="sh-textlink sh-danger" onClick={cancel} disabled={!!busy}>
                {busy === "cancel" ? "Cancelling…" : "Cancel order"}
              </button>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

const isPickupOrder = (o) => String(o.shippingMethod || "").includes("pickup") || !!o.pickupPoint;
const isReady = (o) => o.shipments.some((s) => s.status === "ready_for_pickup");

function Progress({ order: o }) {
  if (o.status === "cancelled") {
    return (
      <ol className="sh-progress is-cancelled">
        <li className="done">
          <span className="sh-progress-dot" />
          <strong>Ordered</strong>
          <em>{dateTime(o.timeline.placed)}</em>
        </li>
        <li className="current bad">
          <span className="sh-progress-dot" />
          <strong>Cancelled</strong>
          <em>{dateTime(o.timeline.cancelled)}</em>
        </li>
      </ol>
    );
  }
  const pickup = isPickupOrder(o);
  const readyAt = o.shipments
    .flatMap((s) => s.events || [])
    .filter((e) => e.status === "ready_for_pickup")
    .map((e) => e.at)
    .sort()[0];
  const t = o.timeline;
  const shipped = t.shipped || (["shipped", "delivered"].includes(o.status) ? o.shipments[0]?.shippedAt : null);
  const delivered = t.delivered || o.shipments.find((s) => s.deliveredAt)?.deliveredAt || (o.status === "delivered" ? "yes" : null);
  const steps = [
    { label: "Ordered", at: t.placed },
    { label: "Paid", at: t.paid },
    { label: "Packed", at: t.packed || (shipped ? "yes" : null) },
    { label: "Shipped", at: shipped },
    pickup && { label: "Ready for pickup", at: readyAt || (isReady(o) || delivered ? "yes" : null) },
    { label: pickup ? "Picked up" : "Delivered", at: delivered },
  ].filter(Boolean);
  const current = steps.reduce((last, s, i) => (s.at ? i : last), 0);
  return (
    <ol className="sh-progress" aria-label="Order progress">
      {steps.map((s, i) => (
        <li key={s.label} className={`${i < current ? "done" : ""}${i === current ? " current" : ""}`}>
          <span className="sh-progress-dot" />
          <strong>{s.label}</strong>
          <em>{s.at && s.at !== "yes" ? dateTime(s.at) : ""}</em>
        </li>
      ))}
    </ol>
  );
}

function Payment({ order, busy, onCard, onBank }) {
  const canvas = useRef(null);
  const p = order.payment;
  const holdUntil = order.holdUntil ? new Date(`${order.holdUntil.replace(" ", "T")}Z`) : null;
  useEffect(() => {
    if (canvas.current && p?.spayd) QRCode.toCanvas(canvas.current, p.spayd, { width: 220, margin: 1 });
  }, [p?.spayd]);
  const hold = holdUntil && (
    <p className="sh-muted sh-small">
      We're holding your items until {holdUntil.toLocaleString("en-GB", { timeZone: "Europe/Prague", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}. After that they go back on sale.
    </p>
  );

  if (p?.type === "bank") {
    return (
      <section className="sh-panel sh-pay">
        <h2 className="sh-h3">Pay by bank transfer</h2>
        <div className="sh-pay-grid">
          <canvas ref={canvas} className="sh-qr" aria-label="Payment QR code" />
          <div>
            <p>Scan the code in your banking app, or enter the details by hand:</p>
            <dl className="sh-paydl">
              <Copyable label="Amount" value={money(p.amountMinor)} copy={(p.amountMinor / 100).toFixed(2)} />
              <Copyable label="Account (IBAN)" value={p.iban} />
              <Copyable label="Variable symbol" value={p.variableSymbol} />
              {p.accountName && <Copyable label="Recipient" value={p.accountName} />}
            </dl>
            <p className="sh-live">
              <span className="sh-live-dot" /> This page updates by itself when the payment lands.
            </p>
            {hold}
            {order.cardAvailable && (
              <button className="sh-textlink" onClick={onCard} disabled={!!busy}>
                {busy === "card" ? "Opening…" : "Pay by card instead"}
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="sh-panel sh-pay sh-pay-card">
      <h2 className="sh-h3">Finish your card payment</h2>
      <p>The payment wasn't completed. Nothing was charged.</p>
      {hold}
      <div className="sh-pay-actions">
        <button className="sh-btn" onClick={onCard} disabled={!!busy}>
          <Icon name="lock" size={16} /> {busy === "card" ? "Opening…" : `Pay ${money(order.totalMinor)} by card`}
        </button>
        {order.bankAvailable !== false && (
          <button className="sh-textlink" onClick={onBank} disabled={!!busy}>
            Pay by bank transfer instead
          </button>
        )}
      </div>
    </section>
  );
}

function Tracking({ order: o }) {
  if (!o.shipments.length) {
    if (["unfulfilled", "fulfilled"].includes(o.status)) {
      return (
        <section className="sh-panel">
          <h2 className="sh-h3">Tracking</h2>
          <p className="sh-muted">The tracking number shows up here the moment your parcel leaves. No need to refresh.</p>
        </section>
      );
    }
    return null;
  }
  return o.shipments.map((s, i) => {
    const events = [...(s.events || [])].reverse();
    return (
      <section key={i} className="sh-panel sh-track">
        <div className="sh-track-head">
          <ShippingIcon methodId={o.shippingMethod} carrier={s.carrier} size={40} />
          <div>
            <h2 className="sh-h3">{s.status_text || HEADLINE[o.status]}</h2>
            <p className="sh-muted sh-small">
              {s.carrierName}
              {s.tracking_number && (
                <>
                  {" · "}
                  <CopyText value={s.tracking_number} />
                </>
              )}
            </p>
          </div>
        </div>
        {events.length > 0 && (
          <ol className="sh-events">
            {events.map((e, n) => (
              <li key={n} className={n === 0 ? "latest" : ""}>
                <time>{dateTime(e.at)}</time>
                <span>
                  {e.text}
                  {e.place ? `, ${e.place}` : ""}
                </span>
              </li>
            ))}
          </ol>
        )}
        <div className="sh-track-foot">
          {s.checkedAt && s.status !== "delivered" && (
            <span className="sh-live">
              <span className="sh-live-dot" /> Updated {timeAgo(s.checkedAt)}
            </span>
          )}
          {s.tracking_url && (
            <a className="sh-textlink" href={s.tracking_url} target="_blank" rel="noreferrer">
              Track on {s.carrierName} <Icon name="external" size={14} />
            </a>
          )}
        </div>
      </section>
    );
  });
}

function Destination({ order: o }) {
  const info = deliveryInfo({ methodId: o.shippingMethod, country: o.pickupPoint?.country || o.shipTo?.country });
  if (!o.shippingMethod) return null;
  return (
    <div className="sh-dest">
      <p className="sh-eyebrow">{isPickupOrder(o) ? "Pickup point" : "Delivery"}</p>
      {o.pickupPoint ? (
        <>
          <strong>{o.pickupPoint.name}</strong>
          <p>{o.pickupPoint.address}</p>
        </>
      ) : (
        <p>
          Home delivery{o.shipTo ? ` to ${[o.shipTo.city, countryName(o.shipTo.country)].filter(Boolean).join(", ")}` : ""}
        </p>
      )}
      {info.how && <p className="sh-muted sh-small">{info.how}</p>}
    </div>
  );
}

function useCopy() {
  const [done, setDone] = useState(false);
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch {
      window.prompt("Copy this:", text);
    }
  };
  return [done, copy];
}

function Copyable({ label, value, copy }) {
  const [done, doCopy] = useCopy();
  return (
    <div>
      <dt>{label}</dt>
      <dd>
        {value}
        <button className="sh-copy" onClick={() => doCopy(copy || value)} aria-label={`Copy ${label}`}>
          <Icon name={done ? "check" : "copy"} size={14} />
        </button>
      </dd>
    </div>
  );
}

function CopyText({ value }) {
  const [done, doCopy] = useCopy();
  return (
    <button className="sh-copytext" onClick={() => doCopy(value)} title="Copy">
      {value} <Icon name={done ? "check" : "copy"} size={12} />
    </button>
  );
}

function SaveLink({ token }) {
  const url = `${window.location.origin}/merch/track/${token}`;
  const [done, copy] = useCopy();
  return (
    <section className="sh-panel sh-save">
      <Icon name="pin" size={20} />
      <div>
        <strong>Keep this page</strong>
        <p className="sh-muted sh-small">It's your order's live status, with tracking once it ships. Bookmark it or copy the link.</p>
      </div>
      <button className="sh-btn sh-btn-ghost" onClick={() => copy(url)}>
        <Icon name={done ? "check" : "copy"} size={16} /> {done ? "Copied" : "Copy link"}
      </button>
    </section>
  );
}
